import { useNavigate } from "react-router-dom";
import { useEvent } from "../context/EventProvider";
import { fallbackOf } from "../settings/catalog";
import { useFlagOr, useLocale, useSettings, useT } from "../settings/context";
import { withContext } from "../routes/registry";
import { Icon } from "../ui/Icon";
import { useAnswers } from "./answers";
import { stamped } from "./answerSession";
import { useSessionKey } from "../auth/useSessionKey";
import { FUNNEL } from "../routes/registry";
import { activeSteps } from "./thread";
import { eachDayChosen, salesDays } from "./articles";
import { datesAnswered, selectedDaysOf } from "./dates";
import { useArticles } from "./useArticles";
import { instanceKey } from "./order";
import { useOffer } from "./useOffre";
import { useMode } from "../panel/mode";
import { isRegistered } from "./participant";
import { useParticipation } from "./useParticipation";

/**
 * THE FUNNEL'S BREADCRUMB — the prototype's `.steps`.
 *
 * The same information twice, because a phone and a desktop cannot show it the
 * same way and the design already says which is which:
 *
 *   .steps__pips  a strip of pips plus a count — all that fits on a phone
 *   .steps__rail  the named steps, a sticky column beside the form on desktop
 *
 * ⚠️ THE STEPS COME FROM THE COMPUTED THREAD, not from the list of routes. A step
 * the settings have taken out must vanish from the breadcrumb too, and the
 * numbering must close up behind it — otherwise the funnel announces "step 2 of 3"
 * and then hands over three of four.
 *
 * ⚠️ CLIQUABLE SEULEMENT UNE FOIS QU'ON A RÉPONDU. Avant, le fil SITUE : il montre où
 * l'on en est dans un parcours qu'on suit d'une étape à l'autre, et rien n'y mène — on
 * avance par « Continuer », on recule par « Retour ». Après, il NAVIGUE : l'inscription
 * existe, tout est coché, tout est atteignable, et quelqu'un qui revient changer une
 * option n'a aucune raison de retraverser six écrans. Une seule bascule, `isRegistered`,
 * au lieu d'une mémoire du parcours que l'état de navigation gardait pour lui seul.
 *
 * Displayed strings go through `t()`, French kept at the call site as the
 * fallback — EXCEPT the messages addressed to the ORGANISER (an empty catalog,
 * a step removed from the thread), French like the rest of the panel.
 */
export function Steps({ path }: { path: string }) {
  const t = useT();
  const { value, days } = useSettings();
  const mode = useMode();
  const [answers] = useAnswers();
  const session = useSessionKey();
  const { recorded, me } = useParticipation();
  const navigate = useNavigate();
  const event = useEvent();

  /**
   * ⚠️ THE RAIL IS THE THREAD, and only the thread — for everyone.
   *
   * It briefly showed the organiser the whole funnel with removed steps struck
   * through, so they would not lose their navigation. Wrong place for it: the rail
   * is what a PARTICIPANT walks, and an organiser reading a crossed-out step has to
   * decode a rail that no longer matches anything. Which steps exist is now settled
   * in one family of the panel ("Étapes du parcours"), and moving between them is
   * the screen picker's job.
   */
  /**
   * ═══ LA SEULE QUESTION QUI COMMANDE LE FIL ═══
   *
   * Une inscription existe (`isRegistered`) : une réponse enregistrée qui VIENT.
   *
   *   FAUSSE  le fil SUIT LE PARCOURS. Ce qui est DERRIÈRE soi est coché et s'y rouvre
   *           d'un clic — la position dans le fil le dit, sans rien avoir à mémoriser —
   *           mais ce qui est DEVANT reste fermé : on ne saute pas par-dessus des
   *           questions dont les suivantes dépendent.
   *   VRAIE   le fil NAVIGUE. Tout est coché, tout s'ouvre : l'inscription existe, il
   *           n'y a plus d'ordre à faire respecter, seulement des corrections à
   *           permettre — et personne ne doit retraverser six écrans pour changer une
   *           option.
   *
   * ⚠️ CE QUE CETTE RÈGLE REMPLACE. Le parcours tenait ses propres comptes dans l'état de
   * navigation : la liste des étapes franchies, puis un témoin d'écriture pour réparer ce
   * que la première avait cassé. Cet état SURVIT AUX RECHARGEMENTS et aux réouvertures
   * d'onglet — il faisait passer pour répondu quelqu'un qui n'avait fait que traverser,
   * et un simple F5 sur la confirmation INSCRIVAIT un compte sans participation. Deux
   * mémoires pour une seule vérité, dont une seule est écrite quelque part ; il ne reste
   * que celle du serveur.
   *
   * ⚠️ ET PAS `responded`, LA COLONNE « RÉPONSE » DU BACK-OFFICE : un refus la pose
   * aussi (bug #522). Revenu sur « je ne viens pas » pour venir, il voyait cochées et
   * ouvertes des étapes qu'il n'avait jamais vues. C'est un premier parcours.
   */
  const registered = isRegistered(recorded);
  const steps = activeSteps(value, useOffer());
  const at = steps.findIndex((s) => s.path === path);
  const here = steps[at];

  /**
   * ⚠️ SAUF AU-DELÀ D'UNE ÉTAPE DES DATES SANS AUCUN JOUR (bug #257). « Continuer » y
   * exige un jour coché ; le fil, lui, ouvrait tout sur `responded` — tout décocher puis
   * cliquer « Confirmation » menait à « Votre inscription est enregistrée » avec le jour
   * qu'on venait de décocher. Ce qui suit l'étape se ferme donc tant qu'elle est vide,
   * OÙ QUE L'ON SOIT dans le fil : revenir d'abord à la participation ne doit pas
   * rouvrir le raccourci. Ce qui la précède reste ouvert — on ne répond pas moins en
   * reculant.
   */
  const datesAt = steps.findIndex((s) => s.key === "dates");
  const datesMissing =
    datesAt >= 0 &&
    !datesAnswered({ chosen: answers.days, saved: recorded?.days });

  /**
   * ⚠️ ET AU-DELÀ D'UNE BILLETTERIE OÙ LE CHOIX OBLIGATOIRE MANQUE, même défaut, même
   * remède (« Un choix obligatoire par journée », 2026-10-27). « Continuer » y exige un
   * article par journée proposée ; le fil, lui, laissait prendre un billet, continuer,
   * revenir tout décocher, puis repartir vers la fin : la confirmation enregistrait une
   * inscription sans billet. La question est `eachDayChosen`, celle du bouton.
   *
   * ⚠️ MÊME ORDRE DE LECTURE QUE CE QUI S'ÉCRIRA : la sélection de ce parcours, sinon la
   * commande enregistrée. Sans réponse dans l'état, la confirmation ne touche pas à la
   * commande ; c'est donc elle qu'on juge.
   *
   * ⚠️ RIEN N'EST FERMÉ TANT QU'ON NE SAIT PAS : catalogue ou commande en vol, ou en
   * échec, le fil reste comme avant. Le fermer le ferait clignoter sous les yeux d'un
   * inscrit à chaque arrivée.
   *
   * ⚠️ ET LA BILLETTERIE NE SE LIT QUE SUR UNE ÉTAPE DU TUNNEL, POUR UN PARTICIPANT
   * CONNECTÉ (relevé par la vérification du 2026-10-27). Ce fil est monté sur TOUS les
   * écrans du châssis (`Shell.tsx`), accueil, sections et album compris, même là où il ne
   * rend rien : sans `at >= 0`, chaque page demandait le catalogue, et la route exige une
   * session, donc un 401 en console pour un visiteur anonyme de l'accueil, à chaque
   * changement de langue. Sans le participant, même 401 sur une étape ouverte au public.
   * Sur une étape, les clés sont celles de la billetterie, de `useStepSave` et du
   * récapitulatif (`useArticles`, `staleTime: Infinity`) : déjà lues dans la visite, rien
   * ne repart ; sinon, une lecture du catalogue et une de la commande.
   */
  const required = useFlagOr("FO_MEETING_V2_ARTICLES_REQUIRED");
  const ticketsAt = steps.findIndex((s) => s.key === "articles");
  const checkTickets =
    mode === "visitor" &&
    required &&
    ticketsAt >= 0 &&
    at >= 0 &&
    typeof me?.id === "number";
  const tickets = useArticles(checkTickets, useLocale());
  const order = answers.articles ?? tickets.saved;
  const comingDays = selectedDaysOf({
    coming: true,
    ...(answers.days ? { chosen: answers.days } : {}),
    ...(recorded?.days ? { saved: recorded.days } : {}),
    all: days.map((d) => d.id),
  }).map((id) => ({ id, label: "" }));
  const ticketsMissing =
    checkTickets &&
    !tickets.loading &&
    !tickets.failure &&
    order !== undefined &&
    !eachDayChosen(salesDays(tickets.articles, comingDays, order), order);

  const lastOpen = Math.min(
    datesMissing ? datesAt : steps.length - 1,
    ticketsMissing ? ticketsAt : steps.length - 1,
  );

  /**
   * ⚠️ SAY WHY IT IS EMPTY. A step the settings have removed has no place in the
   * rail — but an ORGANISER standing on it, configuring it, would otherwise face a
   * blank where the breadcrumb was and read it as "this screen is broken". A
   * participant never gets here: the shell redirects them off it.
   */
  if (!here) {
    /**
     * ⚠️ ONLY ON A DECLARED FUNNEL STEP. `Steps` is rendered for every screen, so
     * without this the note appeared on the HOME screen too — telling an organiser
     * that a page which was never a step had been removed from the funnel. Seen in
     * a screenshot, not in the assertions: nothing was checking a screen outside
     * the funnel.
     */
    const declared = FUNNEL.some((s) => s.path === path);
    if (!declared || mode !== "organiser") return null;
    return (
      <nav
      className="steps"
      data-cfg="steps"
      aria-label={t("PROGRESS", "Progression")}
    >
        <p className="steps__c">
          Cette étape est retirée du parcours — réactivez-la dans « Étapes du
          parcours ».
        </p>
      </nav>
    );
  }

  /**
   * ⚠️ LA CLÉ DE L'INSTANCE, PAS CELLE DE LA FAMILLE. Deux étapes « informations »
   * partagent leur écran et leur clé de base : nommées par elle, elles porteraient
   * FORCÉMENT le même mot dans le fil — renommer la seconde renommerait la première.
   * Mesuré : le fil affichait « Informations » deux fois après renommage.
   */
  /**
   * ⚠️ ET LE REPLI SUR LA CLÉ DE BASE — quatrième fois que la clé ENGENDRÉE se lit
   * naïvement. `fallbackOf` interroge un index bâti sur un événement VIDE : la seconde
   * instance n'y existe pas, donc son repli valait "" et le fil affichait une étape
   * SANS NOM. Mesuré : ["…", "Informations", "", "Société", …].
   */
  /**
   * ⚠️ ET LE REPLI PASSE PAR LE DICTIONNAIRE. `fallbackOf` seul est le mot FRANÇAIS
   * du catalogue : un lecteur anglais voyait « Typologie, Billetterie… » dans un fil
   * qui disait « Progress » (bug #341). Le dictionnaire a ces clés dans chaque langue.
   */
  const nameOf = (screen: { funnel: string; instance?: string }) => {
    const written = value(instanceKey(screen.funnel, screen.instance));
    return typeof written === "string" && written.trim()
      ? written
      : t(screen.funnel, fallbackOf(screen.funnel));
  };

  return (
    <nav
      className="steps"
      data-cfg="steps"
      aria-label={t("PROGRESS", "Progression")}
    >
      <div className="steps__rail">
        {steps.map((screen, i) => {
          /**
           * COCHÉE = ATTEIGNABLE, et c'est la même question posée une fois. Une étape
           * cochée qu'on ne peut pas rouvrir est une invitation qui ne mène nulle part :
           * on corrige ce qu'on vient de répondre en y retournant, pas en recommençant.
           *
           * ⚠️ SUR LE PLUS LOIN ATTEINT, ET NON SUR « AVANT MOI ». Les deux se
           * confondent tant qu'on avance ; ils divergent dès qu'on revient corriger, et
           * la seconde version PUNISSAIT ce retour : revenu sur l'étape 3, on perdait le
           * chemin déjà fait, et il fallait re-valider chaque écran pour retrouver le 4.
           * `loin` est un simple rang, tenu à jour à l'arrivée de chaque étape par le
           * shell — voir `funnel/answers.ts` pour ce qu'il ne fait PAS.
           */
          const filled =
            (registered || i <= (answers.loin ?? -1)) && i <= lastOpen;
          /**
           * ⚠️ THE ORGANISER REACHES EVERY STEP. The gating rule belongs to the
           * PARTICIPANT — it stops somebody answering questions out of order. An
           * organiser answers nothing: applying it to them locked the rail to
           * whatever they happened to have filled in, so the rail was there and led
           * nowhere.
           */
          /* L'organisateur, lui, atteint tout : il configure des écrans, il ne répond
             à rien. */
          const open = i !== at && (mode === "organiser" || filled);
          return (
            <button
              key={screen.path}
              type="button"
              className={`rstep${i === at ? " now" : filled ? " done" : ""}`}
              disabled={!open}
              aria-current={i === at ? "step" : undefined}
              /**
               * ⚠️ LES RÉPONSES VOYAGENT AVEC, et leur absence coûtait cher. Ce bouton
               * naviguait SANS état : revenir sur une étape par le fil vidait tout ce
               * qui avait été répondu dans ce parcours et n'était pas encore écrit — les
               * jours cochés, la commande, le profil. On repartait ensuite vers la
               * confirmation avec un état amputé.
               *
               * Le symptôme observé était pire que la perte elle-même : un participant
               * déjà inscrit qui revenait ici ajouter un article n'avait plus de réponse
               * de participation dans son état, et la confirmation — qui n'écrivait que
               * sur cette réponse-là — n'enregistrait RIEN. L'article semblait
               * simplement ne pas s'ajouter.
               */
              onClick={() =>
                navigate(withContext(screen.path, event?.token), {
                  state: stamped(answers, session),
                })
              }
            >
              {/* A TICK rather than the number once it holds an answer. A number
                  says where a step sits in the order; it says nothing about whether
                  it has been answered — and that is the one thing somebody coming
                  back is looking for. */}
              <i>{filled && i !== at ? <Icon name="check" /> : i + 1}</i>
              <b>{nameOf(screen)}</b>
            </button>
          );
        })}
      </div>

      {/* Decorative on their own — the sentence beside them carries the meaning.

          ⚠️ ONLY THE CURRENT ONE is marked, as the design intends: `.pip.on` widens
          into a BAR among dots, which reads as "you are here". Marking every step
          already crossed turns the whole strip into bars by the last step, and the
          position it exists to show is gone. */}
      <div className="steps__pips" aria-hidden="true">
        {steps.map((screen, i) => (
          <span key={screen.path} className={`pip${i === at ? " on" : ""}`} />
        ))}
      </div>
      {/* ⚠️ UNE SEULE PHRASE À TROUS, PAS TROIS MORCEAUX RECOLLÉS : l'ordre des mots
          n'est pas le même partout, et « Étape » suivi de « sur » figerait le français.
          Voir la convention `$1` de `useT`, héritée de la V1. */}
      <p className="steps__c">
        {t("STEP_N_OF_M", "Étape $1 sur $2 · $3", [
          at + 1,
          steps.length,
          nameOf(here),
        ])}
      </p>
    </nav>
  );
}
